import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageByUrl, RetrievePageResult } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Clickable from "@insite/mobius/Clickable";
import { IconProps } from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorStrings } from "@solveindustrial/Utilities/variables";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface OwnProps {
    blogLink: LinkFieldValue;
    openNewWindow: boolean;
    extendedStyles?: BlogCardStyles;
}

export const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const blogLink = { ...ownProps.blogLink } as any;
    return {
        blogId: blogLink.id,
        blogLink: blogLink.url,
        hasParentId: blogLink.parentId != null,
        parentId: blogLink.parentId,
        currentLanguage: state.context.session.language,
    };
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BlogCardStyles {
    wrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    thumbnail?: InjectableCss;
    textWrapper?: InjectableCss;
    title?: LinkProps;
    date?: TypographyProps;
    summary?: TypographyPresentationProps;
    readMore?: LinkProps;
    readMoreIcon?: IconProps;
}

export const blogCardStyles: BlogCardStyles = {
    wrapper: {
        css: css`
            border: solid 1px ${getColor(colorStrings.commonBorder)};
            height: 100%;
            width: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            height: 100%;
            width: 100%;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    thumbnail: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            background-position: center center;
            background-size: cover;
            background-repeat: no-repeat;
            width: 100%;
            aspect-ratio: 16/9;
        `,
    },
    textWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 10px;
            border-top: solid 1px ${getColor(colorStrings.commonBorder)};
            height: 100%;
            width: 100%;
        `,
    },
    title: {
        typographyProps: {
            forwardAs: "h3",
            size: "16px",
            lineHeight: "24px",
            weight: 700,
            css: css`
                display: block;
                /* stylelint-disable-next-line */
                display: -webkit-box;
                /* stylelint-disable-next-line */
                -webkit-line-clamp: 3;
                /* stylelint-disable-next-line */
                -webkit-box-orient: vertical;
                max-height: 72px;
                overflow-y: hidden;
                position: relative;
                text-decoration: none;
                margin: 0;
            `,
        },
    },
    date: { variant: "legend" },
    summary: {
        mergeCss: true,
        size: "14px",
        lineHeight: "24px",
        css: css`
            display: block;
            /* stylelint-disable-next-line */
            display: -webkit-box;
            /* stylelint-disable-next-line */
            -webkit-line-clamp: 4;
            /* stylelint-disable-next-line */
            -webkit-box-orient: vertical;
            max-height: 96px;
            overflow-y: hidden;
            position: relative;
            margin: 10px 0 0;
        `,
    },
    readMore: {
        icon: {
            iconProps: {
                src: "ChevronRight",
                size: 16,
            },
            position: "right",
        },
        css: css`
            margin: 10px 0 0;
        `,
    },
};

const BlogCard = ({ blogLink, hasParentId, currentLanguage, openNewWindow, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(blogCardStyles, extendedStyles));

    const languageId = `${currentLanguage?.id}`;
    const [thumbnail, setThumbnail] = useState("");
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [publishDate, setPublishDate] = useState("");
    const [hasPublishDate, setHasPublishDate] = useState(false);

    const thumbnailStyle = css`
        ${styles.thumbnail!.css}
        background-image: url("${thumbnail}");
    `;

    const handlePublishDateData = (widgetPublishDate: any) => {
        if (widgetPublishDate != null) {
            const unformatedDate = new Date(widgetPublishDate);
            setPublishDate(
                `${(1 + unformatedDate.getMonth()).toString().padStart(2, "0")}-${unformatedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}-${unformatedDate.getFullYear()}` || "",
            );
        }
    };

    useEffect(() => {
        if (hasParentId) {
            getPageByUrl(blogLink).then((result: RetrievePageResult) => {
                const { page } = result;
                if (page) {
                    const { thumbnail: widgetThumbnail, publishDate: widgetPublishDate } = page.generalFields;
                    const { title: widgetTitle, summary: widgetSummary } = page.translatableFields;
                    setHasPublishDate(widgetPublishDate != null);
                    setThumbnail(widgetThumbnail || "");
                    setTitle(widgetTitle ? widgetTitle[languageId] : "");
                    setSummary(widgetSummary ? widgetSummary[languageId] : "");
                    handlePublishDateData(widgetPublishDate);
                }
            });
        }
    }, []);

    return (
        <StyledWrapper {...styles.wrapper} className="blog-card__component">
            <StyledWrapper {...styles.linkWrapper}>
                <Clickable href={blogLink} target={openNewWindow ? "_blank" : "_self"}>
                    <StyledWrapper css={thumbnailStyle}></StyledWrapper>
                </Clickable>
                <StyledWrapper {...styles.textWrapper}>
                    <Link href={blogLink} target={openNewWindow ? "_blank" : "_self"} {...styles.title}>
                        {title}
                    </Link>
                    {hasPublishDate && <Typography {...styles.date}>{publishDate}</Typography>}
                    <Typography {...styles.summary}>{parse(summary, parserOptions)}</Typography>
                    <Link href={blogLink} target={openNewWindow ? "_blank" : "_self"} {...styles.readMore}>
                        {translate("Read More")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(BlogCard);
